import { useState } from "react";
import { useLang } from "../context/LanguageContext";
import { useForm } from "../context/FormContext";
import type { FormData } from "../context/FormContext";
import Footer from "./Footer";


function hasEnteredData(data: FormData): boolean {
  return !!(
    data.dob.year ||
    data.sex ||
    data.nationality ||
    data.civilStatus ||
    data.street ||
    data.asylumRoutingConfirmed !== null ||
    data.qualifyingGround ||
    data.dataProcessingConsent
  );
}

function getSectionStatus(data: FormData): { completed: number; firstIncomplete: number } {
  const sections = [
    !!(data.firstName && data.primerApellido && data.phone),
    !!(
      data.hasValidPermit &&
      data.asylumApplicant &&
      (data.asylumApplicant === "yes" ||
        (data.asylumApplicant === "no" &&
          (data.irregularSubRoute === "work" ||
            data.irregularSubRoute === "family" ||
            data.irregularSubRoute === "vulnerability"))) &&
      data.hasCriminalRecord &&
      data.previousApplication &&
      data.hasPassport
    ),
    !!(
      data.civilStatus &&
      data.hasSpouse &&
      data.hasChildren &&
      (data.hasChildren === "no" || data.children.some((child) => child.firstName.trim())) &&
      data.hasDependentParents &&
      (data.hasDependentParents === "no" || data.dependentParents.some((parent) => parent.firstName.trim())) &&
      (data.fatherUnknown === "yes" || data.fatherFirstName) &&
      (data.motherUnknown === "yes" || data.motherFirstName)
    ),
    !!(data.street && data.streetNumber && data.floorPiso && data.city && data.province && data.postalCode),
    !!data.dataProcessingConsent,
  ];
  const completed = sections.filter(Boolean).length;
  const firstIncompleteIdx = sections.findIndex((v) => !v);
  return {
    completed,
    firstIncomplete: firstIncompleteIdx === -1 ? 5 : firstIncompleteIdx + 1,
  };
}

interface AdvisorProfileModalProps {
  advisor: string;
  onClose: () => void;
  t: (k: string) => string;
}

function AdvisorProfileModal({ advisor, onClose, t }: AdvisorProfileModalProps) {
  const [profilePhotoError, setProfilePhotoError] = useState(false);
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />

      {/* Sheet / Modal */}
      <div
        className="relative bg-white w-full sm:max-w-[360px] rounded-t-3xl sm:rounded-2xl px-6 pt-8 pb-6 mx-0 sm:mx-4 shadow-xl animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "90vh", overflowY: "auto" }}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 cursor-pointer transition-colors"
        >
          <i className="ri-close-line text-slate-500 text-lg" />
        </button>

        {/* Photo with gradient ring — Instagram/TikTok style */}
        <div className="flex flex-col items-center gap-3 mb-4">
          {/* TODO: Replace this URL with Ana's actual headshot */}
          {profilePhotoError ? (
            <div className="w-28 h-28 rounded-2xl bg-emerald-600 flex items-center justify-center shrink-0 border-2 border-emerald-300">
              <span className="text-white text-[22px] font-bold tracking-wide">AT</span>
            </div>
          ) : (
              <img
                src={LANDING_PHOTO_SRC}
                alt={advisor}
                onError={() => setProfilePhotoError(true)}
                className="w-28 h-28 rounded-2xl object-cover border-2 border-emerald-300"
                style={{ objectPosition: "45% center" }}
              />
          )}

          {/* Name */}
          <h2 className="text-lg font-display text-slate-900 text-center">{advisor}</h2>

          {/* Bio line — role + Venezuela flag */}
          <p className="text-[13px] text-slate-500 text-center -mt-2">
            {t("advisorProfile.bioLine")}
          </p>

          {/* Personal bio block — flowing Instagram-style text */}
          <p className="text-[14px] text-slate-700 leading-relaxed text-center">
            {t("advisorProfile.bioBlock")}
          </p>
        </div>

        {/* Pinned passion statement */}
        <div className="relative bg-slate-50 rounded-xl px-4 py-3 mb-3">
          <div className="w-5 h-5 flex items-center justify-center absolute top-2 right-2">
            <i className="ri-pushpin-line text-slate-300 text-sm" />
          </div>
          <p className="text-[13px] text-slate-600 leading-relaxed pr-5">
            {t("advisorProfile.passion")}
          </p>
        </div>

        {/* Contact line */}
        <p className="text-[13px] text-slate-500 text-center">
          {t("advisorProfile.contact")}
        </p>

        {/* Instagram link */}
        <a
          href="https://www.instagram.com/anatraviesoabogada/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[13px] text-pink-500 hover:text-pink-600 flex items-center justify-center gap-1.5 mt-1 cursor-pointer whitespace-nowrap transition-colors"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-instagram-line text-base" />
          </div>
          {t("advisorProfile.instagram")}
        </a>
      </div>
    </div>
  );
}

interface Props {
  onGetStarted: () => void;
}

const LANDING_PHOTO_SRC = "/Landingpage.jpeg";

export default function LandingScreen({ onGetStarted }: Props) {
  const { t } = useLang();
  const { data, update, goToStep } = useForm();
  const [profileOpen, setProfileOpen] = useState(false);
  const [thumbError, setThumbError] = useState(false);
  const [purposeExpanded, setPurposeExpanded] = useState(false);

  const isReturn = hasEnteredData(data);
  const { completed, firstIncomplete } = getSectionStatus(data);
  const sectionsLeft = 7 - completed;
  const progressPct = Math.round((completed / 7) * 100);

  const firstName = data.firstName || "";
  const advisor = data.advisor || "";
  const advisorFirstName = advisor ? advisor.split(" ")[0] : "";

  const getProgressMessage = (): string => {
    if (sectionsLeft > 3) return t("landing.progress.moreThan3");
    if (sectionsLeft === 3) return t("landing.progress.exactly3");
    if (sectionsLeft === 2) return t("landing.progress.exactly2");
    if (sectionsLeft === 1) return t("landing.progress.exactly1");
    return t("landing.progress.allDone");
  };

  const baseMessage = isReturn
    ? getProgressMessage()
    : advisor
    ? t("landing.firstVisitMessageWithAdvisor")
    : t("landing.firstVisitMessage");

  const handleContinue = () => {
    goToStep(firstIncomplete);
    onGetStarted();
  };

  const handleGetStarted = () => {
    goToStep(1);
    onGetStarted();
  };

  return (
    <>
      {/* Main content - anchored near top */}
      <div className="flex flex-col items-center px-4 pt-6 sm:pt-10 pb-4 w-full">
        <div className="w-full max-w-[420px]">
          {/* Procedure badge */}
          <div className="flex justify-center mb-5">
            <span className="inline-flex items-center gap-2 text-[16px] font-semibold tracking-wide text-slate-700 bg-slate-100 border border-slate-200 rounded-lg px-5 py-3">
              <span className="w-5 h-5 flex items-center justify-center text-base">🇪🇸</span>
              <span className="whitespace-nowrap">{t("landing.procedureBadge")}</span>
            </span>
          </div>

          {/* Greeting */}
          <div className="text-center mb-4">
            <h1 className="text-[26px] font-display text-slate-900 leading-tight">
              {t("landing.greeting")}{" "}
              <span style={{ color: '#059669' }}>{firstName}</span>
              {advisor ? (
                <>, {t("landing.thisIs")} {advisorFirstName}.</>
              ) : (
                <>.</>
              )}
            </h1>
          </div>

          {/* Purpose statement — collapsible */}
          <div className="text-center mb-4">
            <p className="text-[14px] text-slate-500 leading-relaxed">
              {t("landing.purposeShort")}
            </p>
            <div
              className="overflow-hidden transition-all duration-200"
              style={{ maxHeight: purposeExpanded ? "200px" : "0px" }}
            >
              <p className="text-[14px] text-slate-500 leading-relaxed mt-1">
                {t("landing.purposeExpanded")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPurposeExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-emerald-600 bg-slate-50 border border-slate-200 rounded-full px-3 py-1 cursor-pointer hover:bg-slate-100 transition-colors mt-2 whitespace-nowrap"
            >
              {purposeExpanded ? t("landing.less") : t("landing.more")}
              <div className="w-3 h-3 flex items-center justify-center">
                <i className={purposeExpanded ? "ri-arrow-up-s-line text-xs" : "ri-arrow-down-s-line text-xs"} />
              </div>
            </button>
          </div>

          {/* Advisor photo */}
          <div className="mb-4">
            <div className="relative w-full rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 aspect-video">
              {!thumbError ? (
                <img
                  src={LANDING_PHOTO_SRC}
                  alt={advisor || "Advisor photo"}
                  onError={() => setThumbError(true)}
                  className="object-cover w-full h-full opacity-90"
                  style={{ objectPosition: "center 25%" }}
                />
              ) : (
                <div className="w-full h-full bg-emerald-100" />
              )}
            </div>
            <div className="text-center mt-2">
              <p className="text-[13px] font-medium text-slate-700">
                {advisor || t("landing.defaultAdvisorName")}
              </p>
              <p className="text-[11px] text-slate-500 mt-1">{t("landing.videoLength")}</p>
            </div>
          </div>

          {/* Family interest callout */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-4 mb-7">
            <p className="text-[13px] text-slate-600 leading-relaxed mb-3">
              {t("landing.familyInterestText")}
            </p>
            <label
              className={`relative w-full min-h-[40px] flex items-center justify-center rounded-xl text-[13px] font-semibold transition-colors border px-3 ${
                data.familyInterest
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300 cursor-pointer"
              }`}
            >
              <input
                type="checkbox"
                checked={!!data.familyInterest}
                onChange={() => update("familyInterest", true)}
                disabled={data.familyInterest}
                className="sr-only"
                aria-label={t("landing.familyInterestBtn")}
              />
              <span
                className={`absolute left-3 w-4.5 h-4.5 rounded-full border flex items-center justify-center shrink-0 ${
                  data.familyInterest ? "border-emerald-500 bg-emerald-500" : "border-slate-300 bg-white"
                }`}
                aria-hidden
              >
                {data.familyInterest ? <i className="ri-check-line text-white text-[11px]" /> : null}
              </span>
              <span className="text-center">
                {data.familyInterest ? t("landing.familyInterestConfirm") : t("landing.familyInterestBtn")}
              </span>
            </label>
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col gap-3">
            {isReturn ? (
              <button
                type="button"
                onClick={handleContinue}
                className="w-full min-h-[52px] flex items-center justify-center gap-2 rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {t("landing.continue")}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleGetStarted}
                className="w-full min-h-[52px] flex items-center justify-center gap-2 rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {t("landing.getStarted")}
              </button>
            )}
          </div>
        </div>
      </div>
      <Footer />

      {/* Advisor Profile Modal */}
      {profileOpen && advisor && (
        <AdvisorProfileModal
          advisor={advisor}
          onClose={() => setProfileOpen(false)}
          t={t}
        />
      )}

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(40px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up {
          animation: slide-up 0.25s ease-out both;
        }
      `}</style>
    </>
  );
}
